import { axiosApiInstance } from 'Store';
import {
    CalculateFlipApiResponse,
    CalculateFlipResponse,
    CalculatorModuleState,
    GetCalculationApiResponse,
    GetCalculationResponse,
    SaveCalculationRequest,
    SavedCalculation
} from 'utils/types/CalculatorModule';
import { v4 } from 'uuid';

import { activeNotification } from 'components/functions/activeNotification';

import { userApiRequests } from './user';

export const getRenovationByArea = (state: CalculatorModuleState): string => {
    const area = state.area || 0;

    if (area >= 1 && area < 30) {
        return state.renovationFrom1To30Amount || '';
    } else if (area >= 30 && area < 40) {
        return state.renovationFrom30To40Amount || '';
    } else if (area >= 40 && area < 50) {
        return state.renovationFrom40To50Amount || '';
    } else if (area >= 50 && area < 60) {
        return state.renovationFrom50To60Amount || '';
    } else if (area >= 60) {
        return state.renovationFrom60PlusAmount || '';
    }

    return '';
};

const getSavedCalculations = async (): Promise<SavedCalculation[]> => {
    return axiosApiInstance
        .get('/calculation')
        .then((resp) => {
            if (resp.status === 204 || !resp.data) {
                return [];
            }

            const filters = Array.isArray(resp.data) ? resp.data : resp.data.filters || [];

            return filters.map((filter: { filterId: number; name: string; createdAt?: string; updatedAt?: string }) => ({
                calculationId: filter.filterId,
                name: filter.name,
                createdAt: filter.createdAt,
                updatedAt: filter.updatedAt
            }));
        })
        .catch((err) => {
            if (err?.response?.status === 401) {
                window.parent.location.reload();
            }

            const errorId = v4();
            userApiRequests.logException(err, errorId, { functionName: 'getSavedCalculations' });

            return [];
        });
};

const getCalculation = async (calculationId: number): Promise<GetCalculationResponse | null> => {
    return axiosApiInstance
        .get(`/calculation/${calculationId}`)
        .then((resp) => {
            const apiData: GetCalculationApiResponse = resp.data;

            return {
                filterId: apiData.filterId,
                name: apiData.name,
                price: apiData.price,
                certainPrice: apiData.certainPrice,
                area: apiData.area,
                address: apiData.address,
                realPrice: apiData.calculation.realPrice,
                maxPurchasePrice: apiData.calculation.maxPurchasePrice,
                profit: apiData.calculation.profit,
                margin: apiData.calculation.margin,
                costs: apiData.calculation.costs,
                flipTime: apiData.filter.flipTime,
                minProfitAmount: apiData.filter.minProfitAmount,
                minProfitPercent: apiData.filter.minProfitPercent,
                notary: apiData.filter.notary,
                brokerPercent: apiData.filter.brokerPercent,
                rentFees: apiData.filter.rentFees,
                maintenanceFees: apiData.filter.maintenanceFees,
                portalFees: apiData.filter.portalFees,
                photographerAmount: apiData.filter.photographerAmount,
                saleBrokerPercent: apiData.filter.saleBrokerPercent,
                pcc: apiData.filter.pcc,
                renovation: apiData.filter.renovation,
                userOption: apiData.filter.userOption,
                createdAt: apiData.createdAt,
                updatedAt: apiData.updatedAt
            } as GetCalculationResponse;
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId, { functionName: 'getCalculation' });
            activeNotification('Problem z załadowaniem', 'Nie udało się załadować kalkulacji', 'danger');

            return null;
        });
};

const deleteCalculation = (calculationId: number) => {
    return axiosApiInstance
        .delete(`/calculation/${calculationId}`)
        .then(() => {
            activeNotification('Udało się!', 'Kalkulacja została usunięta', 'success');
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId, { functionName: 'deleteCalculation' });
            activeNotification('Problem z usunięciem', 'Nie udało się usunąć kalkulacji', 'danger');
        });
};

const saveCalculation = async (
    params: SaveCalculationRequest
): Promise<{ calculationId: number } | null> => {
    const method = params.filterId ? 'put' : 'post';
    const url = '/calculation';

    return axiosApiInstance({
        method,
        url,
        data: params
    })
        .then((resp) => {
            activeNotification('Udało się!', 'Kalkulacja została zapisana', 'success');

            return { calculationId: resp.data.filterId || params.filterId || 0 };
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId, { functionName: 'saveCalculation' });
            activeNotification('Problem z zapisem', 'Nie udało się zapisać kalkulacji', 'danger');

            return null;
        });
};

const calculateFlip = async (calculatorState: CalculatorModuleState): Promise<CalculateFlipResponse | null> => {
    if (!calculatorState.currentPrice || calculatorState.currentPrice <= 0) {
        return null;
    }

    if (!calculatorState.certainPrice || calculatorState.certainPrice <= 0) {
        return null;
    }

    if (!calculatorState.area || calculatorState.area <= 0) {
        return null;
    }

    const requestBody = {
        address: calculatorState.address || '',
        price: calculatorState.currentPrice,
        area: calculatorState.area,
        certainPrice: calculatorState.certainPrice,

        flipTime: calculatorState.flipTime?.toString() || '',
        minProfitAmount: calculatorState.minProfitAmount?.toString() || '',
        minProfitPercent: calculatorState.minProfitPercent?.toString() || '',
        notary: calculatorState.notary?.toString() || '',
        brokerPercent: calculatorState.brokerPercent?.toString() || '',
        rentFees: calculatorState.rentFees?.toString() || '',
        maintenanceFees: calculatorState.maintenanceFees?.toString() || '',
        portalFees: calculatorState.portalFees?.toString() || '',
        photographerAmount: calculatorState.photographerAmount?.toString() || '',
        saleBrokerPercent: calculatorState.saleBrokerPercent?.toString() || '',
        pcc: calculatorState.pcc?.toString() || '',
        renovation: getRenovationByArea(calculatorState),

        userOption: calculatorState.userOption || 0
    };

    const response = await axiosApiInstance({
        method: 'post',
        url: '/calculation/calculate',
        data: requestBody
    })
        .then((resp) => {
            const apiData: CalculateFlipApiResponse = resp.data;

            return {
                maxPurchasePrice: apiData.calculation.maxPurchasePrice,
                profit: apiData.calculation.profit,
                margin: apiData.calculation.margin,
                costs: apiData.calculation.costs
            } as CalculateFlipResponse;
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId, { functionName: 'calculateFlipGlobal' });
            activeNotification(
                'Błąd obliczeń',
                'Nie udało się obliczyć kalkulacji',
                'warning'
            );

            return null;
        });

    return response;
};

export const calculatorModuleApiRequests = {
    getSavedCalculations,
    getCalculation,
    deleteCalculation,
    saveCalculation,
    calculateFlip
};
